import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type TouchEvent as ReactTouchEvent } from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import styled from "styled-components";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useAppState } from "../../hooks/useAppState";
import { useAutoHideControls } from "../../hooks/useAutoHideControls";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { isIOS, canPlayOnIOS, videoMime, isVideoFilename, setWith, setWithout, formatWatchTogetherCountdown } from "../../../utils";
import { mediaUrl, refreshMediaToken } from "../../../mediaAuth";
import { showToast } from "../../../toast";
import { confirmDialog } from "../../../dialogs";
import { getAutolockBridge } from "../../../autolockBridge";
import { loadAlbums } from "../../../albumsApi";
import { toggleFavoriteApi, deleteMediaApi, loadGalleryData } from "../../../galleryApi";
import { SendGiftDialog } from "../gifts/SendGiftDialog";
import { logVideoPlaybackError, logVideoPlayRejection } from "../../../videoDiagnostics";

const SWIPE_THRESHOLD = 50;

// .media-fs's Paper is a solid black fullscreen stage, not the .glass
// pattern AppDialog provides - this bypasses AppDialog and talks to MUI's
// Dialog directly.
const BlackPaper = styled.div`
  background: #000;
  height: 100%;
  margin: 0;
`;

// No real poster image behind this on purpose: videos have no separate
// thumb URL in this app (GalleryCard.tsx generates its grid poster by
// loading the actual video and seeking to a frame - there's no
// pre-rendered image to blur here), and fetching real frame bytes to blur
// would partially defeat the point of the lock anyway. A tasteful gradient
// + icon reads as "locked" without needing to touch the protected bytes.
const LockOverlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #fff;
  text-align: center;
  padding: 24px;
  background: radial-gradient(circle at 50% 40%, rgba(255, 90, 154, 0.25), transparent 60%), linear-gradient(160deg, #1a0d13, #000);
`;

export function Lightbox() {
  // Aliased - this component already has its own local navigate(direction)
  // for prev/next media, unrelated to react-router's.
  const routerNavigate = useNavigate();
  const currentMediaIndex = useAppState((s) => s.currentMediaIndex);
  const media = useAppState((s) => s.media);
  const favoritesSet = useAppState((s) => s.favoritesSet);
  const togetherSchedules = useAppState((s) => s.togetherSchedules);
  const togetherSession = useAppState((s) => s.togetherSession);
  const currentAlbum = useAppState((s) => s.currentAlbum);

  const [giftDialogOpen, setGiftDialogOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  const open = currentMediaIndex >= 0 && currentMediaIndex < media.length;
  const item = open ? media[currentMediaIndex] : null;
  const isVid = item ? isVideoFilename(item.filename) : false;

  // Re-derived from startsAt directly (not trusted verbatim from the
  // fetched/pushed `status` string forever) - same staleness reasoning as
  // gifts' isGiftLocked: a tab left open past startsAt should unlock
  // itself even if the together_schedule_ready push was somehow missed,
  // since the server's own enforcement is time-based, not push-based.
  // Ticks every 30s (and on tab focus) purely to force this to
  // re-evaluate - the server is what actually decides, this is display
  // only, cosmetic exactly like isGiftLocked's client-side mirror.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    const onFocus = () => forceTick((n) => n + 1);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const lockingSchedule =
    item && isVid
      ? togetherSchedules.find((s) => s.filename === item.filename && s.status === "scheduled" && Date.parse(s.startsAt) > Date.now())
      : null;

  const { hidden: controlsHidden, onActivity, toggle: toggleControls } = useAutoHideControls(isVid);
  const videoRef = useRef<HTMLVideoElement>(null);
  const touchStartX = useRef(0);

  // Mirrored into appState so TogetherRoot.tsx (portaled separately to
  // document.body, no direct relationship to this component tree) can fade
  // its own overlays in sync with these native controls instead of staying
  // permanently visible over the video.
  useEffect(() => {
    appState.mediaControlsHidden = controlsHidden;
    notifyStateChange();
  }, [controlsHidden]);

  // Keyed by filename rather than a plain boolean, so it naturally "resets"
  // for whichever item is open next without a separate effect. A native
  // camera .mov is very often HEVC-encoded (Apple's default since iOS 11),
  // which Chrome/Firefox on non-Apple platforms generally can't decode at
  // all - there's no reliable way to know that from the filename alone
  // (unlike locked-schedule videos, which we can check ahead of time), so
  // this reacts to the <video> element's own real playback failure instead
  // of guessing.
  const [videoFailedFilename, setVideoFailedFilename] = useState<string | null>(null);
  // Video playback audit, Bug D: the media-auth token baked into a
  // playing video's src is frozen at open time (see resolvedSrc's own
  // comment below) and has a hard 60-minute server-side TTL with no
  // "touch on use" the way the cookie session gets - a video opened and
  // left mid-watch for a while can outlive it. A couple of automatic
  // retries (refresh the token, then remount with a freshly computed
  // src, same pattern AvatarImage.tsx/MessageBubble.tsx already use for
  // images) self-heals that instead of permanently showing "can't play
  // this video" for what's really just a stale token. Reset per item via
  // the ref below, not tied to component state, since it must be at 0
  // again the instant a *different* item opens.
  const [videoRetryKey, setVideoRetryKey] = useState(0);
  const videoRetryCountRef = useRef(0);
  const MAX_VIDEO_AUTO_RETRIES = 2;
  // Video playback audit, Bug B: distinguishes "this exact item was still
  // schedule-locked when it became the current item, and just unlocked
  // under it" (no fresh tap behind the unlock - a WS push or the 30s poll
  // timer flipped lockingSchedule, see the autoplay effect below) from "the
  // user just tapped a thumbnail to open this already-unlocked item" (a
  // real gesture, safe to autoplay) - both end with an unlocked <video>
  // mounting, so the autoplay effect alone can't tell them apart without
  // this. Seeded per-item by the effect right below.
  const openedLockedRef = useRef(false);
  const [needsTapToPlay, setNeedsTapToPlay] = useState(false);

  useEffect(() => {
    openedLockedRef.current = !!lockingSchedule;
    videoRetryCountRef.current = 0;
    setVideoRetryKey(0);
    setNeedsTapToPlay(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.filename]);

  function closeLightbox() {
    appState.currentMediaIndex = -1;
    notifyStateChange();
  }

  function navigate(direction: number) {
    const newIndex = currentMediaIndex + direction;
    if (newIndex >= 0 && newIndex < media.length) {
      appState.currentMediaIndex = newIndex;
      notifyStateChange();
    }
  }

  // iOS webm-block check, matching displayMediaAtIndex() exactly.
  useEffect(() => {
    if (!item) return;
    if (isIOS() && !canPlayOnIOS(item.filename)) {
      showToast("This video format (WEBM) does not play on iPhone. Please upload MP4 (H.264/AAC).", "error");
      closeLightbox();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.filename]);

  // Autoplay on open, unmuted (the user just tapped "View"). Also re-runs
  // when a schedule lock clears (lockingSchedule flips truthy->falsy) so a
  // video that unlocks while this exact item is already open picks up
  // autoplay too, not just a freshly-opened one - videoRef.current is null
  // the whole time it's locked (no <video> mounted), so this safely no-ops
  // on every tick until the element actually exists.
  useEffect(() => {
    if (!isVid || !videoRef.current) return;
    const video = videoRef.current;

    if (openedLockedRef.current && !lockingSchedule) {
      // The lock lifted under an item that's already open, with no fresh
      // tap behind it - the single most exposed no-gesture autoplay case
      // found in the video playback audit (Bug B). Surface a tap
      // affordance instead of guessing at an autoplay Safari is likely to
      // silently block; only handle this transition once per item.
      openedLockedRef.current = false;
      setNeedsTapToPlay(true);
      return;
    }

    // play() always returns a Promise per spec in real browsers, but test
    // environments (jsdom has no real media pipeline) can return
    // undefined - guard defensively rather than assume, same fix this
    // session already applied to useAudioPlayer.ts/ReelPlayer.tsx.
    video.muted = false;
    video.play()?.catch((err) => {
      showToast("Couldn't play this video.", "error");
      logVideoPlayRejection("lightbox-autoplay", video, item?.filename || "", err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.filename, isVid, !!lockingSchedule, videoRetryKey]);

  function handleTapToPlay(): void {
    const video = videoRef.current;
    if (!video) return;
    setNeedsTapToPlay(false);
    video.muted = false;
    video.play()?.catch((err) => {
      showToast("Couldn't play this video.", "error");
      logVideoPlayRejection("lightbox-tap-to-play", video, item?.filename || "", err);
    });
  }

  // Hands-off video watching shouldn't burn down the idle timer.
  useEffect(() => {
    if (!isVid || !videoRef.current) return;
    const video = videoRef.current;
    const onPlay = () => getAutolockBridge()?.pauseInactivityForMedia();
    const onPauseOrEnd = () => getAutolockBridge()?.resumeInactivityAfterMedia();
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPauseOrEnd);
    video.addEventListener("ended", onPauseOrEnd);
    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPauseOrEnd);
      video.removeEventListener("ended", onPauseOrEnd);
    };
  }, [isVid]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigate(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        navigate(1);
      } else if (e.key === "Escape") {
        closeLightbox();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentMediaIndex, media.length]);

  function onTouchStart(e: ReactTouchEvent) {
    touchStartX.current = e.changedTouches[0]!.screenX;
    onActivity();
  }

  function onTouchEnd(e: ReactTouchEvent) {
    const diff = touchStartX.current - e.changedTouches[0]!.screenX;
    if (Math.abs(diff) > SWIPE_THRESHOLD) navigate(diff > 0 ? 1 : -1);
  }

  function onStageClick(e: ReactMouseEvent) {
    const target = e.target as HTMLElement;
    if (target.closest("video")) {
      const rect = target.getBoundingClientRect();
      const y = e.clientY - rect.top;
      if (rect.height - y < 80) return; // native video controls strip
    }
    toggleControls();
  }

  async function handleFavorite() {
    if (!item) return;
    try {
      const favorited = await toggleFavoriteApi(item.filename);
      appState.favoritesSet = favorited
        ? setWith(appState.favoritesSet, item.filename)
        : setWithout(appState.favoritesSet, item.filename);
      notifyStateChange();
      loadAlbums();
    } catch (e) {
      showToast((e as Error).message || "Failed to update favorite", "error");
    }
  }

  async function handleDelete() {
    if (!item) return;
    const ok = await confirmDialog({
      title: "Delete this media?",
      message: `Delete "${item.filename.replace(".enc", "")}"? This cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;

    const result = await deleteMediaApi(item.filename);
    if (!result.ok) {
      showToast(result.error, "error");
      return;
    }
    closeLightbox();
    await loadAlbums();
    await loadGalleryData();
  }

  // Comments are per-album, not per-photo (see the product review's
  // inventory) - this opens the album this item is currently being
  // browsed within (whatever appState.currentAlbum is, "all" included),
  // the exact same destination AlbumCard.tsx's own navigation already
  // goes to, so its existing comment thread just works unchanged.
  function handleOpenComments() {
    closeLightbox();
    routerNavigate(`/albums/${currentAlbum}`);
  }

  function handleOpenGift() {
    setGiftDialogOpen(true);
  }

  function handleOpenTogether() {
    appState.togetherInviteDialogOpen = true;
    notifyStateChange();
  }

  // Memoized on filename only, deliberately NOT on the media-auth token -
  // AuthGate.tsx refreshes that token on its own 45-minute interval
  // (refreshMediaToken(), independent of the WS-driven album/gallery/gift/
  // etc. resyncs), regardless of whether this Lightbox is even open.
  // mediaUrl() isn't reactive on its own, but recomputing it inline in the
  // JSX below would pick up that new token on every re-render after a
  // refresh, changing the <video src> string and making the browser reload
  // the video from scratch - restarting playback and wrecking Together
  // Mode sync mid-watch. The token's TTL matches the session TTL (an hour)
  // - far longer than a single open Lightbox session - so there's no
  // correctness reason to ever swap it out from under an already-loading/
  // playing element; only opening a *different* file should ever change
  // this URL. videoRetryKey is the one deliberate exception: it only
  // bumps in response to a real onError (see below), never a passive
  // background refresh, so recomputing there is exactly the "pick up the
  // just-refreshed token" retry Bug D needs, not an unwanted mid-playback
  // reload.
  const resolvedSrc = useMemo(
    () => (item ? mediaUrl(`/assets/media/${item.filename}`) : ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item?.filename, videoRetryKey],
  );

  if (!open || !item) return null;

  const isFav = favoritesSet.has(item.filename);

  return (
    <Dialog
      open
      fullScreen
      onClose={closeLightbox}
      container={() => document.getElementById("react-root")}
      slots={{ paper: BlackPaper }}
      // Together Mode's overlays (TogetherRoot.tsx) are portaled to
      // document.body specifically so they stay genuinely interactive
      // while this Dialog is open (MUI's ModalManager otherwise marks
      // every OTHER child of #react-root aria-hidden, which Chrome
      // treats as unfocusable). That alone wasn't enough, though: MUI's
      // FocusTrap independently yanks focus back into this Dialog
      // whenever it moves anywhere outside this Dialog's own subtree,
      // which a portal doesn't escape (confirmed via a real browser - a
      // real click landed geometrically on the chat input but
      // document.activeElement ended up back on the Dialog instead).
      // disableEnforceFocus turns that off. Scoped to just this Dialog,
      // not a global MUI theme default - every other Dialog in the app
      // keeps normal focus trapping, since none of them need to coexist
      // with other interactive UI the way the Lightbox does.
      disableEnforceFocus
    >
      <div className={`media-fs${controlsHidden ? " media-fs-controls-hidden" : ""}`} style={{ height: "100%" }}>
        <div className="media-fs-top">
          <button type="button" className="media-fs-back" aria-label="Back" onClick={closeLightbox}>
            <i className="bi bi-arrow-left" />
          </button>
          {/* Watch Together polish pass: hidden while a session is active,
              same reasoning as the "Watch together" invite button below
              (editing/deleting the item you're both watching mid-session
              is an edge case, not a real workflow) - and visually, this
              button and TogetherRoot.tsx's own status pill both anchor to
              the same top-right corner, so rendering both together was
              real, unintentional visual clutter competing with the video,
              not a deliberate design choice. Delete itself still works
              exactly the same whenever this IS shown - only its visibility
              during an active session changed. */}
          {!togetherSession && (
            <div className="media-fs-top-right">
              <button
                type="button"
                className="media-fs-icon media-fs-menu"
                aria-label="More"
                onClick={(e) => setMoreAnchor(e.currentTarget)}
              >
                <i className="bi bi-three-dots-vertical" />
              </button>
            </div>
          )}
        </div>

        <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              handleDelete();
            }}
          >
            <i className="bi bi-trash3 me-2" aria-hidden="true" style={{ marginRight: 8 }} />
            Delete
          </MenuItem>
        </Menu>

        <div
          className="modal-body d-flex align-items-center justify-content-center p-0"
          style={{ height: "100%" }}
          onClick={onStageClick}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onMouseMove={onActivity}
        >
          {isVid && lockingSchedule ? (
            // No <video> mounted at all here - not disabled controls, the
            // element that would 401/423 never loads in the first place.
            // The server enforces the real boundary (GET /assets/media/:name
            // 423s until startsAt); this is purely the "movie premiere"
            // presentation on top of that.
            <LockOverlay>
              <i className="bi bi-lock-fill" style={{ fontSize: "2.5rem" }} aria-hidden="true" />
              <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>Scheduled Watch Together</div>
              <div style={{ fontSize: "0.95rem", opacity: 0.9 }}>{formatWatchTogetherCountdown(lockingSchedule.startsAt)}</div>
              <div style={{ fontSize: "0.8rem", opacity: 0.75 }}>
                Locked until {new Date(lockingSchedule.startsAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              </div>
            </LockOverlay>
          ) : isVid && videoFailedFilename === item.filename ? (
            <LockOverlay>
              <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "2.5rem" }} aria-hidden="true" />
              <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>Can't play this video here</div>
              <div style={{ fontSize: "0.9rem", opacity: 0.85 }}>Your browser can't decode this video's format.</div>
              <a
                href={resolvedSrc}
                target="_blank"
                rel="noopener"
                className="btn btn-pink btn-sm"
                style={{ marginTop: 4 }}
              >
                Open it anyway
              </a>
            </LockOverlay>
          ) : isVid ? (
            <>
              <video
                id="mediaModalVideo"
                key={videoRetryKey}
                ref={videoRef}
                playsInline
                controls
                className="w-100 h-100"
                style={{ objectFit: "contain" }}
                preload="metadata"
                src={resolvedSrc}
                onError={(e) => {
                  logVideoPlaybackError("lightbox", e.currentTarget, item.filename);
                  if (videoRetryCountRef.current < MAX_VIDEO_AUTO_RETRIES) {
                    videoRetryCountRef.current += 1;
                    refreshMediaToken().finally(() => setVideoRetryKey((k) => k + 1));
                  } else {
                    setVideoFailedFilename(item.filename);
                  }
                }}
              >
                <source src={resolvedSrc} type={videoMime(item.filename)} />
                Your browser does not support the video element.
              </video>
              {needsTapToPlay && (
                <button type="button" className="media-fs-tap-to-play" onClick={handleTapToPlay}>
                  <i className="bi bi-play-circle-fill" aria-hidden="true" />
                  <span>It's ready — tap to start watching</span>
                </button>
              )}
            </>
          ) : (
            <TransformWrapper initialScale={1} minScale={1} maxScale={4} doubleClick={{ mode: "toggle", step: 1.5 }}>
              <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }}>
                <img
                  src={resolvedSrc}
                  alt={item.filename}
                  className="img-fluid w-100 h-100"
                  style={{ objectFit: "contain" }}
                  loading="lazy"
                />
              </TransformComponent>
            </TransformWrapper>
          )}
        </div>

        {/* Unified action bar - previously just Favorite/Delete, with
            Watch Together floating as a separate top pill (TogetherRoot.tsx)
            and Comment/Gift not reachable from here at all. Delete moved
            into the "More" menu above; everything else that already
            existed somewhere in the app is now reachable from the one
            place every journey in this app passes through.

            Watch Together polish pass: the whole bar is hidden while a
            session is active, not just the "Watch together" entry point
            itself (that part of this conditional already existed) -
            TogetherRoot.tsx's own reaction dock anchors to this exact same
            bottom-of-screen region, and the two together read as two
            competing toolbars stacked on the video rather than one
            deliberate cinema overlay. Favorite/Comments/Gift still work
            identically whenever this bar IS shown (i.e. outside an active
            session) - only its visibility during one changed. */}
        {!togetherSession && (
          <div className="media-fs-bottom">
            <button
              type="button"
              className={`media-fs-action media-fs-fav${isFav ? " is-active" : ""}`}
              aria-label="Favorite"
              onClick={handleFavorite}
            >
              <i className={`bi ${isFav ? "bi-heart-fill" : "bi-heart"}`} />
            </button>
            <button type="button" className="media-fs-action" aria-label="Comments" onClick={handleOpenComments}>
              <i className="bi bi-chat-left-text" />
            </button>
            <button type="button" className="media-fs-action" aria-label="Send as gift" onClick={handleOpenGift}>
              <i className="bi bi-gift" />
            </button>
            <button type="button" className="media-fs-action" aria-label="Watch together" onClick={handleOpenTogether}>
              <i className="bi bi-people" />
            </button>
          </div>
        )}
      </div>

      {item && (
        <SendGiftDialog
          open={giftDialogOpen}
          onClose={() => setGiftDialogOpen(false)}
          presetItem={{ filename: item.filename, type: isVid ? "video" : "image" }}
        />
      )}
    </Dialog>
  );
}
